import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAllVisits } from '../../api/queries';
import { usePracticeInfo, useVisitModals } from '../../hooks/useVisitModals';
import { VisitRow } from '../../components/visits/VisitRow';
import { MediumBadge } from '../../components/common/Badge';
import { Spinner } from '../../components/common/Spinner';
import { apiCall } from '../../api/client';

export function DashboardPage() {
  const { hasActivePlan, status } = usePracticeInfo();
  const { data: visitsData, isLoading } = useAllVisits(hasActivePlan);
  const activeVisits = visitsData?.activeVisits || [];
  const upcomingVisits = visitsData?.upcomingVisits || [];
  const { openDetail, modals } = useVisitModals();

  // Next Up
  const nextUp = useMemo(() => {
    const now = Date.now();
    const fifteenMinAgo = now - 15 * 60 * 1000;
    const tenMinFromNow = now + 10 * 60 * 1000;

    const candidates = [
      ...upcomingVisits.filter((v) => v.scheduledAt && v.scheduledAt >= fifteenMinAgo),
      ...activeVisits.filter((v) => v.isScheduled && v.scheduledAt && v.scheduledAt >= fifteenMinAgo),
    ].sort((a, b) => (a.scheduledAt || 0) - (b.scheduledAt || 0));

    const seen = new Set<string>();
    const unique = candidates.filter((v) => {
      const id = v.visitId || v.roomId || '';
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    let next = unique[0] || null;
    if (next && (next.scheduledAt || 0) < now) {
      const soonVisit = unique.find((v) => v.scheduledAt && v.scheduledAt >= now && v.scheduledAt <= tenMinFromNow);
      if (soonVisit) next = soonVisit;
    }
    return next;
  }, [activeVisits, upcomingVisits]);

  // Countdown
  const [countdownText, setCountdownText] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  useEffect(() => {
    if (!nextUp?.scheduledAt) return;
    function update() {
      const raw = (nextUp!.scheduledAt || 0) - Date.now();
      if (raw <= 0) {
        const ago = Math.floor(-raw / (1000 * 60));
        setCountdownText(ago < 1 ? 'starts now' : `started ${ago} min ago`);
        return;
      }
      const days = Math.floor(raw / (1000 * 60 * 60 * 24));
      const h = Math.floor((raw % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const m = Math.floor((raw % (1000 * 60 * 60)) / (1000 * 60));
      if (days > 0) setCountdownText(`starts in ${days}d ${h}h`);
      else if (h > 0) setCountdownText(`starts in ${h}h ${m}m`);
      else if (m < 1) setCountdownText('starts now');
      else setCountdownText(`starts in ${m} min`);
    }
    update();
    const interval = setInterval(update, 10000);
    return () => clearInterval(interval);
  }, [nextUp]);

  // Today schedule
  const todayVisits = useMemo(() => {
    const now = Date.now();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(todayStart);
    todayEnd.setDate(todayEnd.getDate() + 1);

    const instants = activeVisits
      .filter((v) => !v.isScheduled && v.status !== 'ended' && v.status !== 'completed' && v.status !== 'cancelled')
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

    const futureToday = upcomingVisits
      .filter((v) => v.scheduledAt && v.scheduledAt >= now && v.scheduledAt < todayEnd.getTime())
      .sort((a, b) => (a.scheduledAt || 0) - (b.scheduledAt || 0));

    const oneHourAgo = now - 60 * 60 * 1000;
    const activeScheduled = activeVisits
      .filter((v) => v.isScheduled && v.scheduledAt && v.scheduledAt >= oneHourAgo)
      .sort((a, b) => (a.scheduledAt || 0) - (b.scheduledAt || 0));

    // Deduplicate: a started scheduled visit appears in both activeVisits and upcomingVisits
    const activeIds = new Set(activeScheduled.map((v) => v.visitId || v.roomId).filter(Boolean));
    const dedupedFuture = futureToday.filter((v) => {
      const id = v.visitId || v.roomId || '';
      return !id || !activeIds.has(id);
    });
    const scheduledToday = [...activeScheduled, ...dedupedFuture].sort((a, b) => (a.scheduledAt || 0) - (b.scheduledAt || 0));
    return [...instants, ...scheduledToday];
  }, [activeVisits, upcomingVisits]);

  const videoCount = activeVisits.filter((v) => (v.medium || 'video') === 'video').length;
  const phoneCount = activeVisits.filter((v) => v.medium === 'phone').length;
  const smsCount = activeVisits.filter((v) => v.medium === 'sms').length;

  return (
    <>
      <div className="widget-grid">
        {/* Next Up */}
        <div id="nextUpCard">
          {nextUp ? (
            <div
              className="next-up-card"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', padding: '1.25rem 1.5rem', cursor: 'pointer' }}
              onClick={() => openDetail(nextUp)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3>Next Up</h3>
                <div className="next-up__patient">{nextUp.patientName || 'Visit'}</div>
                <div className="next-up__meta">
                  <MediumBadge medium={nextUp.medium || 'video'} />
                  {nextUp.visitType && <span style={{ marginLeft: '0.5rem' }}>{nextUp.visitType}</span>}
                  {nextUp.scheduledAt && (
                    <span style={{ marginLeft: '0.5rem' }}>
                      {new Date(nextUp.scheduledAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                      {' at '}
                      {new Date(nextUp.scheduledAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  )}
                  <span style={{ marginLeft: '0.5rem' }}>({nextUp.duration || 30} min)</span>
                </div>
              </div>
              <div className="next-up__countdown" style={{ flexShrink: 0 }}>
                {countdownText} <span style={{ fontSize: '0.5em', verticalAlign: 'middle', position: 'relative', top: '-0.5px', marginLeft: '0.5em' }}>&#9654;</span>
              </div>
            </div>
          ) : status === 'none' || status === 'canceled' ? (
            <div className="next-up-card next-up-card--static" style={{ padding: '1.25rem 1.5rem' }}>
              <h3>Get Started</h3>
              <div style={{ fontSize: '0.9375rem', marginBottom: '0.75rem' }}>Start your free 7-day trial to begin seeing patients.</div>
              <button className="btn btn--sm" style={{ background: '#fff', color: 'var(--primary-color)', fontWeight: 600 }} disabled={checkoutLoading} onClick={async () => {
                setCheckoutLoading(true);
                try {
                  const data = await apiCall<{ url: string }>('/billing/checkout', { method: 'POST' });
                  window.location.href = data.url;
                } catch { window.location.href = '/settings#practice'; }
              }}>{checkoutLoading ? <><span style={{ visibility: 'hidden' }}>Start Free Trial</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Start Free Trial'}</button>
            </div>
          ) : activeVisits.length === 0 ? (
            <div className="next-up-card next-up-card--static" style={{ padding: '1.25rem 1.5rem' }}>
              <h3>Next Up</h3>
              <div style={{ fontSize: '0.9375rem' }}>You don't have any visits yet. Schedule your first one or start an instant visit.</div>
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
              <p className="text-muted text-sm">Nothing coming up</p>
            </div>
          )}
        </div>

        {/* Active Visits */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h2 style={{ margin: 0 }}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: '0.35rem' }}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
              Active Visits
            </h2>
            {activeVisits.length > 0 && (
              <Link to="/active" className="text-sm" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>View all &rarr;</Link>
            )}
          </div>
          {isLoading ? <Spinner /> : activeVisits.length === 0 ? (
            <p className="text-muted text-sm">No active visits. Use the Instant Visit button to start one.</p>
          ) : (
            <div className="active-summary">
              <Link to="/active" className="active-summary__card">
                <div className="active-summary__count" style={{ color: '#1E40AF' }}>{videoCount}</div>
                <div className="active-summary__label">Video</div>
              </Link>
              <Link to="/active" className="active-summary__card">
                <div className="active-summary__count" style={{ color: '#166534' }}>{phoneCount}</div>
                <div className="active-summary__label">Phone</div>
              </Link>
              <Link to="/active" className="active-summary__card">
                <div className="active-summary__count" style={{ color: '#0D9488' }}>{smsCount}</div>
                <div className="active-summary__label">SMS</div>
              </Link>
            </div>
          )}
        </div>

        {/* Today's Schedule */}
        <div className="card">
          <h2 className="mb-2">
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: '0.35rem' }}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="10" y1="14" x2="14" y2="14" /></svg>
            Today's Schedule
          </h2>
          {isLoading ? <Spinner /> : todayVisits.length === 0 ? (
            <p className="text-muted text-sm">No visits scheduled for today. Use the Schedule Visit button to add one.</p>
          ) : (
            todayVisits.map((v, i) => (
              <VisitRow
                key={v.roomId || v.visitId || i}
                visit={v}
                index={i}
                onClick={() => openDetail(v)}
                timeWidth="120px"
                timeCol={!v.isScheduled && !v.scheduledAt ? <span style={{ color: 'var(--accent-color)', fontWeight: 600 }}>Now</span> : undefined}
              />
            ))
          )}
        </div>
      </div>

      {modals}
    </>
  );
}
